import React, { useEffect, useRef, useState } from 'react';
import styles from './Impersonation.module.scss';
import { ScreenComponent } from 'new/routes/RouterManager';
import * as PATHS from 'js/constants/pathnames';
import PageLayout from 'new/elements/pageLayout/PageLayout';
import { Table } from 'new/elements/Table';
import { useOrgs } from 'new/hooks/adapters/useOrgs';
import { columns } from 'new/components/settings/Impersonation/columns';
import { Autocomplete, IconButton, InputAdornment, Stack, Tooltip } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { TextField } from 'new/elements/TextField';
import { useDistance } from 'new/hooks/useDistance';
import { User } from 'new/entities/User';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { ReactComponent as IncognitoIcon } from 'new/components/icons/incognito.svg';
import { ReactComponent as IncognitoOff } from 'new/components/icons/incognitoOff.svg';
import { fetchOrgMembers, impersonate } from 'js/actions';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_ORG_MEMBERS_REQUEST, IMPERSONATION_REQUEST } from 'js/constants/actionTypes';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { useHistory } from 'react-router-dom';
import UserPage from 'new/components/settings/userPage/UserPage';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import { useImpersonatedUser } from 'new/hooks/adapters/useImpersonatedUser';
import { useImpersonateUsers } from 'new/components/settings/Impersonation/useImpersonateUsers';
import useAppDispatch from 'js/hooks/useAppDispatch';

const RightView = ({ orgs, onOrgChange, selectedOrg, onSearchChange }) => {
  return (
    <Stack className={styles.rightView} flexDirection="row" gap="16px">
      <Autocomplete
        value={selectedOrg}
        options={orgs}
        getOptionLabel={(option: User) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disableClearable
        filterSelectedOptions
        onChange={onOrgChange}
        renderInput={(params) => (
          <TextField {...params} value={selectedOrg?.name || ''} placeholder="Users" />
        )}
      />
      <TextField
        onChange={onSearchChange}
        placeholder="Search users"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
    </Stack>
  );
};

export const Impersonation: ScreenComponent<{}> = () => {
  const pageRef = useRef<HTMLDivElement>();
  const [selectedOrg, setSelectedOrg] = useState(null);
  const { users, onSearchChange } = useImpersonateUsers(selectedOrg?.id);

  const orgs = useOrgs();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const currentOrg = useCurrentOrg();
  const user = useCurrentUser();
  const impersonated = useImpersonatedUser();
  const loading = useIsLoading(IMPERSONATION_REQUEST, GET_ORG_MEMBERS_REQUEST);
  const distance = useDistance(pageRef, 400);

  useEffect(() => {
    if (selectedOrg?.id) {
      dispatch(fetchOrgMembers(selectedOrg.id));
    }
  }, [selectedOrg]);

  useEffect(() => {
    if (!selectedOrg) {
      setSelectedOrg(orgs.find((org) => org.id === currentOrg.id));
    }
  }, [orgs]);

  useDidUpdateEffect(() => {
    if (!loading) {
      if (!user || !user.super_user || !!impersonated) {
        history.replace(UserPage.screenConfig.path);
      }
    }
  }, [loading]);

  const onClickImpersonate = (member: User) => {
    dispatch(impersonate(member?.id, selectedOrg.id));
  };

  const onOrgChange = (_, org) => {
    setSelectedOrg(org);
  };

  return (
    <PageLayout
      ref={pageRef}
      title="Impersonation"
      className={styles.container}
      description="Impersonate a user to get issues resolved quickly"
      rightView={
        <RightView
          orgs={orgs}
          onSearchChange={onSearchChange}
          onOrgChange={onOrgChange}
          selectedOrg={selectedOrg}
        />
      }
    >
      <Table
        rows={users}
        loading={loading}
        height={distance}
        columns={columns}
        rowEndRender={(row: User) => {
          const isCurrentUser = row?.email === user?.email;

          if (isCurrentUser) {
            return (
              <Tooltip
                title={
                  selectedOrg.id === currentOrg.id
                    ? 'Cannot impersonate the current user'
                    : 'Change Active Org in the account menu dropdown to switch to this org'
                }
              >
                <IconButton className={styles.impersonateAction}>
                  <IncognitoOff />
                </IconButton>
              </Tooltip>
            );
          }

          return (
            <IconButton
              className={styles.impersonateAction}
              onClick={() => onClickImpersonate(row)}
            >
              <IncognitoIcon />
            </IconButton>
          );
        }}
        getRowClassName={(row) =>
          row?.org_membership_status === 'ACTIVE' ? styles.rowBase : styles.rowDeactivated
        }
      />
    </PageLayout>
  );
};

Impersonation.screenConfig = {
  path: `${PATHS.ROUTE_REDESIGN}/settings/impersonation`,
  screenName: 'Impersonation',
  pageTitle: 'Impersonation',
  header: {
    title: 'Settings',
    slugTitle: 'Impersonation',
  },
  domain: 'settings',
};
